import { useSyncExternalStore } from "react";
import type { GettingStartedTaskKey } from "@/constants/gettingStarted";

/**
 * "Finished?" prompt state, for the sidebar dock.
 *
 * WHY THIS IS NOT REACT STATE
 * The whole point of the prompt is that it survives a navigation: a step's CTA
 * sends the user to /profile, and the dock has to still know, on that page,
 * which step sent them. Component state dies at the route change, and the
 * dock is remounted by the sidebar on every page.
 *
 * WHY NOT THE SERVER
 * "I clicked through to my profile a moment ago" is not a fact about the
 * account, it is a fact about this tab in the last few minutes. Persisting it
 * would mean the prompt reappearing tomorrow on a different machine, asking
 * about a trip the user does not remember taking.
 *
 * sessionStorage is the honest fit: it survives navigation and reload, dies
 * with the tab, and is per-tab, so two tabs cannot argue about which step the
 * user is mid-way through.
 *
 * Every read and write is guarded — sessionStorage throws in private windows
 * and when site data is blocked. Losing this state degrades to "no prompt",
 * which is the same as not having the feature, so it is never worth an error.
 */

const KEY = "fa_gs_return_v1";

/** Nudges are capped per session, not per lifetime — see `recordNudge`. */
export const MAX_NUDGES = 3;

/**
 * How long the user has to be away from the flow before the dock opens itself.
 *
 * Five minutes, and the gap is measured BETWEEN nudges, not from page load —
 * dismissing one starts the next interval. Drop this to ~15s to watch the
 * whole budget play out locally.
 */
export const NUDGE_INTERVAL_MS = 5 * 60 * 1000;

export interface ReturnPromptState {
  /** The step whose CTA navigated the user away, if it is still unfinished. */
  ret: GettingStartedTaskKey | null;
  /**
   * Steps the user answered "Not yet" about. Not asked again this session
   * unless the step's CTA is used again (see `setReturnStep`).
   */
  hidden: GettingStartedTaskKey[];
  /** How many times the dock has auto-opened itself this session. */
  nudges: number;
}

const EMPTY: ReturnPromptState = { ret: null, hidden: [], nudges: 0 };

const read = (): ReturnPromptState => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<ReturnPromptState>;
    return {
      ret: typeof parsed.ret === "string" ? (parsed.ret as GettingStartedTaskKey) : null,
      hidden: Array.isArray(parsed.hidden) ? (parsed.hidden as GettingStartedTaskKey[]) : [],
      nudges: typeof parsed.nudges === "number" ? parsed.nudges : 0,
    };
  } catch {
    return EMPTY;
  }
};

// The snapshot has to be referentially stable between writes, or
// useSyncExternalStore re-renders forever. So it is cached here and only
// replaced when something actually changes.
let cached: ReturnPromptState = read();
const listeners = new Set<() => void>();

const write = (next: ReturnPromptState) => {
  cached = next;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — the prompt simply will not survive a reload */
  }
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSnapshot = () => cached;

/**
 * Called when a step's CTA routes the user somewhere else.
 *
 * Also lifts an earlier "Not yet" for that step: going back through the CTA
 * is the user starting the step again, so the Done / Not yet prompt has to be
 * waiting on the page it lands on. Without this, one "Not yet" silenced the
 * prompt for the rest of the tab's life.
 */
export const setReturnStep = (key: GettingStartedTaskKey) => {
  if (cached.ret === key && !cached.hidden.includes(key)) return;
  write({ ...cached, ret: key, hidden: cached.hidden.filter((k) => k !== key) });
};

export const clearReturnStep = () => {
  if (cached.ret === null) return;
  write({ ...cached, ret: null });
};

/** "Not yet" — stop asking about this step until its CTA is used again. */
export const hideStep = (key: GettingStartedTaskKey) => {
  write({
    ...cached,
    ret: cached.ret === key ? null : cached.ret,
    hidden: cached.hidden.includes(key) ? cached.hidden : [...cached.hidden, key],
  });
};

/**
 * Count an auto-open against the session's budget.
 *
 * Capped at MAX_NUDGES and scoped to the tab on purpose: a nudge is a
 * reminder, and a reminder that keeps coming back after being dismissed three
 * times has stopped being a reminder. A fresh session is a fair place to try
 * again; the same session is not.
 */
export const recordNudge = () => {
  write({ ...cached, nudges: cached.nudges + 1 });
};

export const useReturnPrompt = (): ReturnPromptState =>
  useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
